/* eslint-disable complexity */
/* eslint-disable max-len */
import React from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import { appUrl } from "../../../constants/url-constants";
import { NoCarImageFoundPath } from "../../shared/no-car-image-found";
import { getCarName } from "../../../utils/helpers/car-properties.ae";
import { fastlyUrl, imageTypes } from "../../../utils/helpers/cdn-image-url";
import { carDetailUrlAu } from "../../../utils/helpers/get-detail-page-url";

const JSON_INDENT_SPACES = 2;

const getDetailsPageUrls = ({ year, make, model, appointmentId }) => {
    const { relativeURL } = carDetailUrlAu({ year, carName: `${make || ""} ${model || ""}`, carId: appointmentId });
    const relativeUrlLowercase = relativeURL.toLowerCase();
    const detailsPageUrl = `${appUrl()}${relativeUrlLowercase}`;
    let canonicalUrl = detailsPageUrl;
    if (canonicalUrl[canonicalUrl.length - 1] !== "/") {
        canonicalUrl += "/";
    }
    return { detailsPageUrl, canonicalUrl };
};

const getPrimaryImageUrl = (config, mainImage, isView360Enabled) => {
    const hasImage = mainImage && mainImage.path;
    return hasImage ? fastlyUrl(
        config.imageHost,
        mainImage.path,
        isView360Enabled ? imageTypes.gsCarCard360 : imageTypes.gsCarCard
    ) : NoCarImageFoundPath;
};

const getDisplayCarName = (carDetails) => {
    return `${getCarName(carDetails)} ${carDetails.variant}`;
};

const isListingSold = (booked, listingActive) => {
    return booked || !listingActive;
};

const buildCarJsonLd = ({
    year,
    make,
    model,
    variant,
    bodyType,
    fuelType,
    transmissionType,
    egc,
    price,
    isSold,
    detailsPageUrl,
    odometerReading,
    imageUrl
}) => {
    const nameParts = [year, make, model, variant, fuelType ? String(fuelType).toUpperCase() : ""].filter(Boolean);
    const schema = {
        "@context": "https://schema.org",
        "@type": "Car",
        name: nameParts.join(" "),
        brand: { "@type": "Brand", name: make || "" },
        model: model || "",
        vehicleModelDate: year || "",
        ...(bodyType ? { bodyType } : {}),
        ...(fuelType ? { fuelType } : {}),
        ...(transmissionType ? { vehicleTransmission: transmissionType } : {}),
        offers: {
            "@type": "Offer",
            priceCurrency: "AUD",
            price: Number(egc || price || 0),
            itemCondition: "https://schema.org/UsedCondition",
            availability: isSold ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            url: detailsPageUrl
        },
        ...((typeof odometerReading !== "undefined" && odometerReading !== null) ? {
            mileageFromOdometer: {
                "@type": "QuantitativeValue",
                value: odometerReading,
                unitCode: "KMT"
            }
        } : {}),
        ...(imageUrl ? { image: [imageUrl] } : {})
    };
    return JSON.stringify(schema, null, JSON_INDENT_SPACES);
};

const MetaTagsCarDetailsV2 = ({ carDetails, cityName, config }) => {
    const { mainImage, threeSixtyViewUrl, year, make, model, appointmentId, variant, odometerReading, bodyType, booked, listingActive, reserved, transmissionType, fuelType, egc, price } = carDetails;
    const { detailsPageUrl, canonicalUrl } = getDetailsPageUrls({ year, make, model, appointmentId });
    const isView360Enabled = !!threeSixtyViewUrl;
    const imageUrl = getPrimaryImageUrl(config, mainImage, isView360Enabled);
    const carName = getDisplayCarName(carDetails);
    const isSold = isListingSold(booked, listingActive);

    const shouldHideSchema = isSold || reserved;

    const carSchema = !shouldHideSchema ? buildCarJsonLd({
        year,
        make,
        model,
        variant,
        bodyType,
        fuelType,
        transmissionType,
        egc,
        price,
        isSold,
        detailsPageUrl,
        odometerReading,
        imageUrl
    }) : null;

    return (
        <Helmet>
            <title>{`Used ${carName} ${odometerReading}KM Driven Car in Australia`}</title>
            <meta name="ROBOTS" content={isSold ? "NOINDEX,FOLLOW" : "INDEX,FOLLOW"} />
            <meta name="title" content={`Used ${carName} ${odometerReading} KM Driven Car in Australia`} />
            <meta name="description"
                content={`Upgrade your driving experience with this ${year} ${make} ${model} ${variant} in Australia. With ${odometerReading} KM Driven and impressive features, this ${bodyType} is perfect for work or play.`} />
            <meta name="keywords"
                content={`Buy used ${carName}, buy second hand ${carName}, used ${carName} in ${cityName}, second hand ${carName} from ${cityName}, 2nd hand ${carName} online in ${cityName}, pre-owned ${carName} in ${cityName}`} />
            <meta property="og:title" content={`Used ${carName} in ${cityName} - Second Hand ${carName} in ${cityName} for Sale`} />
            <meta property="og:description"
                content={`Used ${carName} car in ${cityName} for Sale - Cars24 offers 100% verified second hand cars in ${cityName}. Avail old cars at best price along with Easy Finance & RC Transfer.`} />
            <meta property="og:url" content={detailsPageUrl} />
            <link href={canonicalUrl} rel="canonical" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Cars24" />
            { imageUrl && <meta property="og:image" content={imageUrl} /> }
            { carSchema && <script type="application/ld+json">{carSchema}</script> }
        </Helmet>
    );
};

MetaTagsCarDetailsV2.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,
    cityName: PropTypes.string.isRequired,
    carDetails: PropTypes.object,
    config: PropTypes.object,
    countryCode: PropTypes.string
};

export default withRouter(MetaTagsCarDetailsV2);
